import React, { FC } from 'react';
import { Typography, Box, Stack, Theme, SxProps, styled } from '@mui/material';

import { formatter } from '../../utils/helpers/formatters';
import { FavoriteIcon, FavoriteFilledIcon } from '../../icons';
import { IProduct } from '../../interfaces/product';
import { useAppSelector, useAppStore } from '../../hooks/reduxHooks';
import { tGridTemplateColumns } from '../../react-pages/HomePage/OurProducts/OurProducts';
import { addOrRemoveFavoriteProducts, isFavorite } from '../../utils/localStorageUtils';
import { AlumniSansFont } from '../../styles/fonts';
import { getShortCondition } from '../../utils/helpers/dataHelpers';

export const InfoStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '2px',
  minHeight: '34px',
  [theme.breakpoints.down('md768')]: {
    flexDirection: 'column',
  },
}));

export const TitleStyled = styled(Typography)({
  textTransform: 'uppercase',
  color: '#666666',
  lineHeight: '15px !important',
  minHeight: '30px',
  fontFamily: AlumniSansFont.style.fontFamily,
});

export const ModelStyled = styled(Typography)({
  flex: 1,
  fontFamily: AlumniSansFont.style.fontFamily,
});

export const DescriptionWrapperStyled = styled(Stack)(({ theme }) => ({
  width: '100%',
  gap: '0px',
  [theme.breakpoints.down('md768')]: {
    gap: '2px',
  },
}));

type CardDetailsProps = {
  product: IProduct;
  isWishlist?: boolean;
  noPrice?: boolean;
  columns: tGridTemplateColumns;
  handleWishList: () => void;
};

export const CardDetails: FC<CardDetailsProps> = ({ product, isWishlist, noPrice, columns, handleWishList }) => {
  const { selectedCurrency } = useAppSelector((st) => st.currency);
  const store = useAppStore();

  const WishListIcon = ({ boxSx, className }: { boxSx?: SxProps<Theme>; className?: string }) => (
    <Box sx={boxSx} className={className} onClick={() => addOrRemoveFavoriteProducts(product, store)}>
      {isFavorite(product, store) ? <FavoriteFilledIcon size={19} /> : <FavoriteIcon size={19} />}
    </Box>
  );

  return (
    <Stack flexDirection='row' justifyContent={'space-between'} gap={1}>
      <DescriptionWrapperStyled flex={1}>
        <InfoStyled>
          <ModelStyled
            sx={{
              lineHeight: { xs: '24px', md768: '33.6px' },
              fontSize: { xs: '20px', md768: '28px' },
              letterSpacing: '1px',
            }}
          >
            {product?.model?.brand?.name}
          </ModelStyled>
          <Typography
            sx={{
              lineHeight: { xs: '24px', md768: '33.6px' },
              fontSize: { xs: '20px', md768: '28px' },
              fontFamily: AlumniSansFont.style.fontFamily,
              letterSpacing: '1px',
            }}
          >
            {!noPrice ? (product?.price ? formatter(product?.price, selectedCurrency) : 'Price on request') : null}
          </Typography>
        </InfoStyled>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box sx={{ display: 'flex', gap: '3px', flexDirection: 'column' }}>
            <TitleStyled
              sx={{
                lineHeight: { xs: '20px', md768: '16px' },
                fontSize: { xs: '16px', md768: '20px' },
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {product?.model?.name && <span>{product?.model?.name}</span>}
              <small>{getShortCondition(product?.condition)}</small>
            </TitleStyled>
          </Box>

          <WishListIcon
            boxSx={{ visibility: { md768: 'hidden' }, display: { xs: 'none', md: 'block' } }}
            className='wishlist-icon'
          />
        </Box>
      </DescriptionWrapperStyled>
      <WishListIcon boxSx={{ display: { xs: 'flex', md768: 'none', maxHeight: '20px' } }} />
    </Stack>
  );
};
